import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Check, MapPin, Edit3, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

const POLL_MS = 15000;

const actionMeta = {
  created: { icon: MapPin, label: "ha aggiunto", color: "text-emerald-600 bg-emerald-50" },
  updated: { icon: Edit3, label: "ha modificato", color: "text-blue-600 bg-blue-50" },
  deleted: { icon: Trash2, label: "ha eliminato", color: "text-rose-600 bg-rose-50" },
};

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const secs = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
  if (secs < 60) return `${secs}s fa`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m fa`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h fa`;
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/notifications", { params: { limit: 30 } });
      setItems(r.data.items);
      setUnread(r.data.unread);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, [load]);

  const markAll = async () => {
    try { await api.post("/notifications/mark-read", { all: true }); load(); } catch {}
  };

  const openItem = async (n) => {
    if (!n.read) {
      try { await api.post("/notifications/mark-read", { ids: [n.id] }); } catch {}
    }
    setOpen(false);
    if (n.board_id) navigate(`/boards/${n.board_id}`);
    load();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 rounded-full hover:bg-slate-100 flex items-center justify-center transition-colors"
        >
          <Bell className="w-4 h-4 text-slate-700" />
          {unread > 0 && (
            <span
              data-testid="notification-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 rounded-full bg-[#FF3B30] text-white text-[9px] font-bold flex items-center justify-center px-1"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0 rounded-2xl overflow-hidden border-slate-200">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-sm">Notifiche</div>
            <div className="text-[11px] text-slate-500">{unread === 0 ? "Nessuna non letta" : `${unread} non lette`}</div>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" className="rounded-full h-8 text-[11px]" onClick={markAll} data-testid="mark-all-read">
              <Check className="w-3.5 h-3.5 mr-1" /> Segna tutte
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 && (
            <div className="py-10 text-center text-xs text-slate-500">Nessuna notifica ancora.</div>
          )}
          {items.map((n) => {
            const meta = actionMeta[n.action] || actionMeta.updated;
            const Icon = meta.icon;
            return (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                data-testid={`notif-${n.id}`}
                className={`w-full text-left flex gap-3 p-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${!n.read ? "bg-blue-50/40" : ""}`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs leading-snug">
                    <span className="font-semibold">{n.actor_name}</span>
                    <span className="text-slate-600"> {meta.label} </span>
                    <span className="font-mono-num uppercase text-slate-900 truncate">{n.target_desc}</span>
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5 flex items-center gap-1.5">
                    <span>in</span>
                    <span className="font-medium">{n.board_name}</span>
                    <span>·</span>
                    <span>{timeAgo(n.created_at)}</span>
                  </div>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#007AFF] shrink-0 mt-2" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
